import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocaleState, useTranslate } from 'react-admin';
import {
  Box, Card, Grid, Skeleton, Stack, TableBody, TableCell, TableHead, TableRow,
  Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import { httpClient } from '@/utils/httpClient';
import { useActiveSection } from '@/layouts/useSectionTabs';
import {
  BlockHeading, DataTable, DatagridEmpty, KpiTile, PageTitle, RecordCardList, SectionHeader, StatusDot,
} from '@/components/shell';
import { SystemOverview } from '@/features/tech-support/components/SystemOverview';
import { KycServicesMonitor } from '@/features/tech-support/components/KycServicesMonitor';
import { StressTestPanel } from '@/features/tech-support/components/StressTestPanel';
import type { StatEntry } from '@/features/tech-support/types';
import {
  errorRate, healthLabelKey, healthTone, periodCounts, sumPeriod, useStatFormat,
} from '@/features/statistics/accountHealth';

dayjs.extend(relativeTime);

/** How many accounts the weekly chart draws; the table below lists them all. */
const CHART_ACCOUNTS = 8;

type CountKey =
  | 'accounts' | 'servers' | 'licenses' | 'documentTypes' | 'nationalities'
  | 'kycFields' | 'users' | 'fieldMappings' | 'defaultMappings' | 'costCenters';

/** Each count as it arrives: `undefined` while it loads, `null` when it could not be read. */
type SystemCounts = Partial<Record<CountKey, number | null>>;

/** The record count of a list endpoint, or `null` when it could not be read — never a made-up 0. */
const fetchCount = async (url: string): Promise<number | null> => {
  try {
    const r = await httpClient.get<{ recordsTotal?: number } | unknown[]>(
      `${url}?draw=1&length=1&start=0&search=&column=&orderBy=asc`,
    );
    if (Array.isArray(r.data)) return r.data.length;
    return r.data?.recordsTotal ?? null;
  } catch {
    return null;
  }
};

/** The users count: its own endpoint, which does not resolve each user's role as the list does. */
const fetchUserCount = async (): Promise<number | null> => {
  try {
    const r = await httpClient.get<{ count?: number }>('/admin/user/count');
    return typeof r.data?.count === 'number' ? r.data.count : null;
  } catch {
    return null;
  }
};

/** Where each count comes from. They load and render independently: a slow one holds up nothing else. */
const COUNT_SOURCES: ReadonlyArray<[CountKey, () => Promise<number | null>]> = [
  ['accounts', () => fetchCount('/admin/account/GetAccounts')],
  ['servers', () => fetchCount('/admin/server/GetServers')],
  ['licenses', () => fetchCount('/admin/license/GetLicenses')],
  ['documentTypes', () => fetchCount('/admin/document-type/GetDocumentTypes')],
  ['nationalities', () => fetchCount('/admin/nationality/GetNationality')],
  ['kycFields', () => fetchCount('/admin/kyc-field/GetKYCFields')],
  ['users', fetchUserCount],
  ['fieldMappings', () => fetchCount('/admin/fields-mapping/GetMappingFields')],
  ['defaultMappings', () => fetchCount('/admin/default-mapping/GetDefaultMappings')],
  ['costCenters', () => fetchCount('/admin/cost-center/GetCostCenters')],
];

const dayTotal = (s: StatEntry) => {
  const { ok, err } = periodCounts(s, 'day');
  return ok + err;
};

const weekTotal = (s: StatEntry) => {
  const { ok, err } = periodCounts(s, 'week');
  return ok + err;
};

/* ── Weekly requests by account ── */

const LegendItem: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    <Box component="span" aria-hidden sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: color }} />
    <Typography component="span" variant="body2" color="text.secondary">{label}</Typography>
  </Box>
);

const SummaryFigure: React.FC<{ label: string; value: string; align?: 'left' | 'center' | 'right' }> = ({ label, value, align = 'left' }) => (
  <Box sx={{ textAlign: align }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body1" sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
  </Box>
);

const WeekByAccountChart: React.FC<{ stats: StatEntry[] | null; loading: boolean }> = ({ stats, loading }) => {
  const t = useTranslate();
  const theme = useTheme();
  const fmt = useStatFormat();
  const okColor = theme.palette.primary.main;
  const errColor = theme.custom.status.problem.dot;

  const rows = useMemo(
    () => (stats ?? [])
      .map((s) => ({ name: s.accountName, ...periodCounts(s, 'week') }))
      .filter((r) => r.ok + r.err > 0)
      .sort((a, b) => (b.ok + b.err) - (a.ok + a.err))
      .slice(0, CHART_ACCOUNTS),
    [stats],
  );
  const week = sumPeriod(stats ?? [], 'week');
  const weekAll = week.ok + week.err;
  const max = Math.max(...rows.map((r) => r.ok + r.err), 1);

  return (
    <Box component="section" aria-labelledby="dashboard-week-chart" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <BlockHeading
        id="dashboard-week-chart"
        title={t('screens.dashboard.weekByAccount')}
        subtitle={t('screens.dashboard.weekByAccountScope', { count: CHART_ACCOUNTS })}
      />
      <Card sx={{ p: 2, flex: 1 }}>
        {loading ? (
          <Stack spacing={1.5}>
            {[...Array(5)].map((_, i) => <Skeleton key={i} height={32} />)}
          </Stack>
        ) : stats === null ? (
          <DatagridEmpty title={t('screens.dashboard.statsUnavailable')} hint={t('screens.dashboard.statsUnavailableHint')} />
        ) : rows.length === 0 ? (
          <DatagridEmpty title={t('screens.dashboard.noWeekRequests')} hint={t('screens.dashboard.noWeekRequestsHint')} />
        ) : (
          <>
            {/* Legend above the plot, in the order of the stack. */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <LegendItem color={okColor} label={t('dashboard.chart.success')} />
              <LegendItem color={errColor} label={t('dashboard.chart.errors')} />
            </Box>
            <Stack spacing={1.5}>
              {rows.map((row) => {
                const total = row.ok + row.err;
                return (
                  <Box key={row.name}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                      <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>{row.name}</Typography>
                      <Typography variant="body2" sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                        {row.err > 0
                          ? t('screens.dashboard.barLabel', { total: fmt.count(total), err: fmt.count(row.err) })
                          : fmt.count(total)}
                      </Typography>
                    </Box>
                    {/* Bars start at zero and share one scale: the busiest account. */}
                    <Box
                      role="img"
                      aria-label={t('screens.dashboard.barAria', { name: row.name, ok: row.ok, err: row.err })}
                      sx={{ display: 'flex', height: 10, width: `${(total / max) * 100}%`, minWidth: 4, borderRadius: '2px', overflow: 'hidden' }}
                    >
                      <Box sx={{ width: `${(row.ok / total) * 100}%`, backgroundColor: okColor }} />
                      <Box sx={{ width: `${(row.err / total) * 100}%`, backgroundColor: errColor }} />
                    </Box>
                  </Box>
                );
              })}
            </Stack>
            <Box
              sx={{
                display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap',
                mt: 2, pt: 1.5, borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <SummaryFigure label={t('dashboard.chart.weekTotal')} value={fmt.count(weekAll)} />
              <SummaryFigure label={t('dashboard.chart.avgDaily')} value={fmt.count(Math.round(weekAll / 7))} align="center" />
              <SummaryFigure
                label={t('dashboard.chart.successRate')}
                value={weekAll > 0 ? fmt.rate((week.ok / weekAll) * 100) : '—'}
                align="right"
              />
            </Box>
          </>
        )}
      </Card>
    </Box>
  );
};

/* ── Resources configured ── */

const SystemResources: React.FC<{ counts: SystemCounts }> = ({ counts }) => {
  const t = useTranslate();
  const fmt = useStatFormat();
  const items: Array<{ label: string; value: number | null | undefined }> = [
    { label: t('dashboard.resources.accounts'), value: counts['accounts'] },
    { label: t('dashboard.resources.servers'), value: counts['servers'] },
    { label: t('dashboard.resources.licenses'), value: counts['licenses'] },
    { label: t('dashboard.resources.docTypes'), value: counts['documentTypes'] },
    { label: t('dashboard.resources.nationalities'), value: counts['nationalities'] },
    { label: t('dashboard.resources.kycFields'), value: counts['kycFields'] },
    { label: t('dashboard.resources.users'), value: counts['users'] },
    { label: t('dashboard.resources.mappings'), value: counts['fieldMappings'] },
    { label: t('dashboard.resources.defaults'), value: counts['defaultMappings'] },
    { label: t('dashboard.resources.costCenters'), value: counts['costCenters'] },
  ];

  return (
    <Box component="section" aria-labelledby="dashboard-resources" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <BlockHeading id="dashboard-resources" title={t('dashboard.systemResources')} subtitle={t('screens.dashboard.resourcesScope')} />
      <Card sx={{ p: 2, flex: 1 }}>
        <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 2, rowGap: 1.5 }}>
          {items.map((item) => (
            <Box key={item.label} sx={{ minWidth: 0 }}>
              <Typography component="dt" variant="body2" color="text.secondary" noWrap>{item.label}</Typography>
              <Typography component="dd" variant="body1" sx={{ m: 0, fontVariantNumeric: 'tabular-nums' }}>
                {item.value === undefined ? <Skeleton width={32} /> : item.value !== null ? fmt.count(item.value) : '—'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Card>
    </Box>
  );
};

/* ── Account activity ── */

const AccountHealth: React.FC<{ entry: StatEntry }> = ({ entry }) => {
  const t = useTranslate();
  const fmt = useStatFormat();
  const { ok, err } = periodCounts(entry, 'day');
  const rate = errorRate(ok, err);
  return <StatusDot tone={healthTone(rate)} label={`${t(healthLabelKey(rate))} · ${fmt.rate(rate)}`} />;
};

/** Today's requests as a bar on the scale of the busiest account today, with the two counts in words. */
const TodayBar: React.FC<{ entry: StatEntry; max: number }> = ({ entry, max }) => {
  const theme = useTheme();
  const { ok, err } = periodCounts(entry, 'day');
  const total = ok + err;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flex: 1, minWidth: 80 }}>
        {total > 0 && (
          <Box aria-hidden sx={{ display: 'flex', height: 8, width: `${(total / max) * 100}%`, minWidth: 4, borderRadius: '2px', overflow: 'hidden' }}>
            <Box sx={{ width: `${(ok / total) * 100}%`, backgroundColor: theme.palette.primary.main }} />
            <Box sx={{ width: `${(err / total) * 100}%`, backgroundColor: theme.custom.status.problem.dot }} />
          </Box>
        )}
      </Box>
      <Typography variant="body2" component="span" sx={{ whiteSpace: 'nowrap' }}>
        <TodayCounts entry={entry} />
      </Typography>
    </Box>
  );
};

const LastAccess: React.FC<{ entry: StatEntry }> = ({ entry }) => {
  const t = useTranslate();
  const [locale] = useLocaleState();
  if (!entry.lastAccess) return <>{t('dashboard.activity.never')}</>;
  return <>{dayjs(entry.lastAccess).locale(locale === 'es' ? 'es' : 'en').fromNow()}</>;
};

const TodayCounts: React.FC<{ entry: StatEntry }> = ({ entry }) => {
  const t = useTranslate();
  const fmt = useStatFormat();
  const { ok, err } = periodCounts(entry, 'day');
  return <>{t('screens.dashboard.okErr', { ok: fmt.count(ok), err: fmt.count(err) })}</>;
};

const AccountActivity: React.FC<{ stats: StatEntry[] | null; loading: boolean }> = ({ stats, loading }) => {
  const t = useTranslate();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const fmt = useStatFormat();

  const sorted = useMemo(() => [...(stats ?? [])].sort((a, b) => dayTotal(b) - dayTotal(a)), [stats]);
  const maxDay = Math.max(...sorted.map(dayTotal), 1);
  const title = t('dashboard.activity.title');

  const empty = stats === null
    ? <DatagridEmpty title={t('screens.dashboard.statsUnavailable')} hint={t('screens.dashboard.statsUnavailableHint')} />
    : <DatagridEmpty title={t('screens.dashboard.noActivity')} hint={t('screens.dashboard.noActivityHint')} />;

  return (
    <Box component="section" aria-labelledby="dashboard-activity">
      <BlockHeading id="dashboard-activity" title={title} subtitle={t('screens.dashboard.activityScope')} />
      {loading ? (
        <Card sx={{ p: 2 }}>
          {[...Array(5)].map((_, i) => <Skeleton key={i} height={40} />)}
        </Card>
      ) : isPhone ? (
        <RecordCardList
          records={sorted}
          getKey={(s) => `${s.accountName}-${s.api}`}
          empty={empty}
          mobile={{
            primary: (s) => s.accountName,
            status: (s) => <AccountHealth entry={s} />,
            lines: [
              { label: t('screens.dashboard.today'), render: (s) => <TodayCounts entry={s} /> },
              { label: t('dashboard.activity.week'), render: (s) => fmt.count(weekTotal(s)) },
              { label: t('dashboard.activity.lastAccess'), render: (s) => <LastAccess entry={s} /> },
            ],
          }}
        />
      ) : (
        <DataTable label={title} empty={sorted.length === 0 ? empty : undefined}>
          <TableHead>
            <TableRow>
              <TableCell>{t('dashboard.activity.account')}</TableCell>
              <TableCell>{t('dashboard.activity.health')}</TableCell>
              <TableCell sx={{ minWidth: 220 }}>{t('dashboard.activity.todaySuccessError')}</TableCell>
              <TableCell align="right">{t('dashboard.activity.week')}</TableCell>
              <TableCell align="right">{t('dashboard.activity.month')}</TableCell>
              <TableCell>{t('dashboard.activity.lastAccess')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((s) => {
              const month = periodCounts(s, 'month');
              return (
                <TableRow key={`${s.accountName}-${s.api}`}>
                  <TableCell>
                    <Box>{s.accountName}</Box>
                    <Typography variant="caption" color="text.secondary">{s.api}</Typography>
                  </TableCell>
                  <TableCell><AccountHealth entry={s} /></TableCell>
                  <TableCell><TodayBar entry={s} max={maxDay} /></TableCell>
                  <TableCell align="right">{fmt.count(weekTotal(s))}</TableCell>
                  <TableCell align="right">{fmt.count(month.ok + month.err)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title={s.lastAccess ? dayjs(s.lastAccess).format('DD/MM/YYYY HH:mm') : ''}>
                      <span><LastAccess entry={s} /></span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </DataTable>
      )}
    </Box>
  );
};

/* ── The panel ── */

const DashboardContent = () => {
  const t = useTranslate();
  const fmt = useStatFormat();
  const [counts, setCounts] = useState<SystemCounts>({});
  const [stats, setStats] = useState<StatEntry[] | null>([]);
  const [statsLoading, setStatsLoading] = useState(true);
  // Identifies the latest load: answers of a superseded load (or after unmount) are dropped.
  const generation = useRef(0);

  const load = useCallback(() => {
    const current = ++generation.current;
    setCounts({});
    setStatsLoading(true);

    // Every count and the statistics are requested at once and shown as each one answers.
    COUNT_SOURCES.forEach(([key, read]) => {
      read().then((value) => {
        if (generation.current === current) setCounts((prev) => ({ ...prev, [key]: value }));
      });
    });

    httpClient.get<{ data?: StatEntry[] }>(
      '/admin/account/GetAccountStatistics?draw=1&length=50&start=0&search=&column=&orderBy=desc',
    ).then(
      (sr) => { if (generation.current === current) setStats(sr.data?.data || []); },
      () => { if (generation.current === current) setStats(null); },
    ).finally(() => {
      if (generation.current === current) setStatsLoading(false);
    });
  }, []);

  useEffect(() => {
    load();
    return () => { generation.current += 1; };
  }, [load]);

  const countsLoading = COUNT_SOURCES.some(([key]) => counts[key] === undefined);
  const loading = statsLoading;

  const today = sumPeriod(stats ?? [], 'day');
  const totalDay = today.ok + today.err;
  const errorRateToday = errorRate(today.ok, today.err);

  const mostActive = useMemo(() => {
    if (!stats || stats.length === 0) return null;
    // Today first; with no activity today, the busiest account of the week.
    const topDay = [...stats].sort((a, b) => dayTotal(b) - dayTotal(a))[0];
    if (dayTotal(topDay) > 0) return { name: topDay.accountName, requests: dayTotal(topDay), period: 'today' as const };
    const topWeek = [...stats].sort((a, b) => weekTotal(b) - weekTotal(a))[0];
    if (weekTotal(topWeek) > 0) return { name: topWeek.accountName, requests: weekTotal(topWeek), period: 'week' as const };
    return null;
  }, [stats]);

  const statsKnown = stats !== null;

  return (
    <Box>
      <SectionHeader
        left={<PageTitle title={t('dashboard.heading')} subtitle={t('screens.dashboard.subtitle')} />}
        onRefresh={load}
        refreshing={statsLoading || countsLoading}
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <KpiTile
            label={t('dashboard.kpi.requestsToday')}
            value={statsKnown ? fmt.compact(totalDay) : null}
            detail={statsKnown ? t('screens.dashboard.requestsDetail', { ok: fmt.count(today.ok), err: fmt.count(today.err) }) : undefined}
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <KpiTile
            label={t('dashboard.kpi.errorRate')}
            value={statsKnown && totalDay > 0 ? fmt.rate(errorRateToday) : null}
            detail={statsKnown && totalDay > 0
              ? <StatusDot tone={healthTone(errorRateToday)} label={t('screens.dashboard.rateBase', { err: fmt.count(today.err), total: fmt.count(totalDay) })} />
              : statsKnown ? t('screens.dashboard.noRequestsToday') : undefined}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiTile
            label={t('dashboard.kpi.mostActive')}
            textValue
            value={mostActive?.name ?? null}
            detail={mostActive
              ? `${fmt.compact(mostActive.requests)} ${mostActive.period === 'week' ? t('dashboard.kpi.requestsThisWeek') : t('screens.dashboard.requestsTodayLower')}`
              : statsKnown ? t('screens.dashboard.noRecentActivity') : undefined}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiTile
            label={t('dashboard.kpi.totalAccounts')}
            value={counts.accounts != null ? fmt.compact(counts.accounts) : null}
            detail={counts.servers != null ? `${fmt.count(counts.servers)} ${t('dashboard.kpi.serversConfigured')}` : undefined}
            loading={counts.accounts === undefined}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <WeekByAccountChart stats={stats} loading={loading} />
        </Grid>
        <Grid item xs={12} md={4}>
          <SystemResources counts={counts} />
        </Grid>
      </Grid>

      <AccountActivity stats={stats} loading={loading} />
    </Box>
  );
};

/** The dashboard destination: the panel, and for advanced users and
 *  administrators the system sections the section bar offers next to it. */
export const Dashboard = () => {
  switch (useActiveSection()) {
    case 'overview':
      return <SystemOverview />;
    case 'services':
      return <KycServicesMonitor />;
    case 'stress-test':
      return <StressTestPanel />;
    default:
      return <DashboardContent />;
  }
};

export default Dashboard;
